import { usePolicyContext } from '@/components/ecom-policy/policy/Policy.context'
import type { GreenpoolAsset, Product } from '@/components/ecom-policy/types'
import { useCart } from '@/contexts/cart/cart-context'

import { usePolicyId } from './use-policy-id'

export const useSelectProduct = () => {
  const policyCtx = usePolicyContext()
  const policyId = usePolicyId()

  const { addItem, removeItem, addGreenpoolItem, removeGreenpoolItem, setQty } = useCart()

  const [selectedProductIds, setSelectedProductIds] = policyCtx.selectedProductIds
  const [productQuantityById, setProductQuantityById] = policyCtx.productQuantityById
  const [selectedAccessoriesIds, setSelectedAccessoriesIds] = policyCtx.selectedAccessoriesIds
  const [selectedGreenpoolProductId, setSelectedGreenpoolProductId] =
    policyCtx.selectedGreenpoolProductId

  const handleProductSelect = ({
    product,
    onModalClose,
  }: {
    product: Product
    onModalClose?: () => void
  }) => {
    if (selectedProductIds?.includes(product.id)) {
      const filteredSelectedProductIds = selectedProductIds?.filter(
        selectedProductIdItem => selectedProductIdItem !== product.id
      )
      setSelectedProductIds(filteredSelectedProductIds)
      setProductQuantityById({ ...productQuantityById, [product.id]: 0 })

      // Remove from cart
      removeItem(policyId, product.id)
      setQty(policyId, product.id, 0)
    } else {
      setSelectedProductIds([...(selectedProductIds || []), product.id])
      setProductQuantityById({ ...productQuantityById, [product.id]: 1 })

      // Add to cart
      addItem(policyId, product)
      setQty(policyId, product.id, 1)
    }

    if (onModalClose) {
      onModalClose()
    }
  }

  const handleGroupedProductSelect = ({
    product,
    groupSelectedProductId,
    setGroupSelectedProductId,
    onModalClose,
  }: {
    product: Product
    groupSelectedProductId: number | null
    setGroupSelectedProductId: (v: number | null) => void
    onModalClose?: () => void
  }) => {
    if (!groupSelectedProductId) {
      setSelectedProductIds([...(selectedProductIds || []), product.id])
      setGroupSelectedProductId(product.id)

      // Add to cart
      addItem(policyId, product)
      setQty(policyId, product.id, 1)

      if (onModalClose) {
        onModalClose()
      }
      setProductQuantityById({ ...productQuantityById, [product.id]: 1 })
      return
    }

    if (groupSelectedProductId === product.id) {
      const filteredSelectedProductIds = selectedProductIds?.filter(
        selectedProductIdItem => selectedProductIdItem !== product.id
      )
      setSelectedProductIds(filteredSelectedProductIds || null)
      setGroupSelectedProductId(null)
      setProductQuantityById({ ...productQuantityById, [product.id]: 0 })

      // Remove from cart
      removeItem(policyId, product.id)
      setQty(policyId, product.id, 0)
      return
    }

    // Remove previously selected product from cart
    const filteredSelectedProductIds =
      selectedProductIds?.filter(
        selectedProductIdItem => selectedProductIdItem !== groupSelectedProductId
      ) || []

    setSelectedProductIds([...filteredSelectedProductIds, product.id])
    setProductQuantityById({ ...productQuantityById, [product.id]: 1 })
    setGroupSelectedProductId(product.id)

    // Remove previously selected product from cart
    removeItem(policyId, groupSelectedProductId)
    setQty(policyId, groupSelectedProductId, 0)

    // Add newly selected product to cart
    addItem(policyId, product)
    setQty(policyId, product.id, 1)
  }

  const handleAccessorySelect = ({
    product,
    onModalClose,
  }: {
    product: Product
    onModalClose?: () => void
  }) => {
    if (selectedAccessoriesIds?.includes(product.id)) {
      const filteredSelectedAccessoriesIds = selectedAccessoriesIds?.filter(
        selectedAccessoryIdItem => selectedAccessoryIdItem !== product.id
      )
      setSelectedAccessoriesIds(filteredSelectedAccessoriesIds)
      setProductQuantityById({ ...productQuantityById, [product.id]: 0 })

      // Remove from cart
      removeItem(policyId, product.id)
      setQty(policyId, product.id, 0)
    } else {
      setSelectedAccessoriesIds([...(selectedAccessoriesIds || []), product.id])
      setProductQuantityById({ ...productQuantityById, [product.id]: 1 })

      // Add to cart
      addItem(policyId, product)
      setQty(policyId, product.id, 1)
    }
    if (onModalClose) {
      onModalClose()
    }
  }

  const handleGreenpoolProductSelect = ({ asset }: { asset: GreenpoolAsset }) => {
    if (selectedGreenpoolProductId === asset.id) {
      setSelectedGreenpoolProductId(null)
      // Remove from cart
      removeGreenpoolItem(policyId, asset.id)
    } else {
      setSelectedGreenpoolProductId(asset.id)
      // Add to cart
      addGreenpoolItem(policyId, asset)
    }
  }

  return {
    handleProductSelect,
    handleGroupedProductSelect,
    handleAccessorySelect,
    handleGreenpoolProductSelect,
  }
}

export const useSelectProductInWizard = (policyId: number) => {
  if (!policyId) {
    throw new Error('Policy ID is required')
  }
  const {
    addItem,
    removeItem,
    getGreenpoolItem,
    addGreenpoolItem,
    removeGreenpoolItem,
    setQty,
    getProductsByPolicy,
    getAccessoriesByPolicy,
  } = useCart()
  const selectedGreenpoolItem = getGreenpoolItem(policyId)
  const selectedGreenpoolProductId = Number(selectedGreenpoolItem?.productId)

  const selectedProductIds = getProductsByPolicy(policyId).map(item => Number(item.productId))
  const selectedAccessoriesIds = getAccessoriesByPolicy(policyId).map(item =>
    Number(item.productId)
  )

  const handleProductSelect = ({
    product,
    onModalClose,
  }: {
    product: Product
    onModalClose?: () => void
  }) => {
    if (selectedProductIds?.includes(product.id)) {
      // Remove from cart
      removeItem(policyId, product.id)
      setQty(policyId, product.id, 0)
    } else {
      // Add to cart
      addItem(policyId, product)
      setQty(policyId, product.id, 1)
    }

    if (onModalClose) {
      onModalClose()
    }
  }

  const handleGroupedProductSelect = ({
    product,
    groupSelectedProductId,
    setGroupSelectedProductId,
    onModalClose,
  }: {
    product: Product
    groupSelectedProductId: number | null
    setGroupSelectedProductId: (v: number | null) => void
    onModalClose?: () => void
  }) => {
    if (!groupSelectedProductId) {
      setGroupSelectedProductId(product.id)
      // Add to cart
      addItem(policyId, product)
      setQty(policyId, product.id, 1)

      if (onModalClose) {
        onModalClose()
      }
      return
    }

    if (groupSelectedProductId === product.id) {
      setGroupSelectedProductId(null)
      // Remove from cart
      removeItem(policyId, product.id)
      setQty(policyId, product.id, 0)
      return
    }
    setGroupSelectedProductId(product.id)

    // Add to cart
    addItem(policyId, product)
    setQty(policyId, product.id, 1)
  }

  const handleAccessorySelect = ({
    product,
    onModalClose,
  }: {
    product: Product
    onModalClose?: () => void
  }) => {
    if (selectedAccessoriesIds?.includes(product.id)) {
      // Remove from cart
      removeItem(policyId, product.id)
      setQty(policyId, product.id, 0)
    } else {
      // Add to cart
      addItem(policyId, product)
      setQty(policyId, product.id, 1)
    }
    if (onModalClose) {
      onModalClose()
    }
  }

  const handleGreenpoolProductSelect = ({ asset }: { asset: GreenpoolAsset }) => {
    if (selectedGreenpoolProductId === asset.id) {
      // Remove from cart
      removeGreenpoolItem(policyId, asset.id)
    } else {
      // Add to cart
      addGreenpoolItem(policyId, asset)
    }
  }

  return {
    handleProductSelect,
    handleGroupedProductSelect,
    handleAccessorySelect,
    handleGreenpoolProductSelect,
  }
}
